import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useLocation, useSearchParams, Link } from 'react-router-dom'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { AlertCircle, BookOpen, GraduationCap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GoogleIcon } from '../icons/GoogleIcon'

export default function LoginPage() {
  const { t } = useTranslation('common')
  const { signInWithGoogle } = useAuth()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedMarketing, setAcceptedMarketing] = useState(false)

  useEffect(() => {
    // Check for error parameters
    const errorParam = searchParams.get('error')
    if (errorParam) {
      switch (errorParam) {
        case 'oauth_error':
          setError(t('login.oauthError'))
          break
        case 'no_code':
          setError(t('login.noCode'))
          break
        case 'auth_failed':
          setError(t('login.authFailed'))
          break
        case 'auth_error':
          setError(t('login.authError'))
          break
        case 'terms_required':
          setError(t('login.termsRequired'))
          break
        case 'new_user_needs_signup':
          setError(t('login.newUserNeedsSignup'))
          break
        default:
          setError(t('login.unknownError'))
      }
    }
  }, [searchParams, t])

  const handleGoogleAuth = async (isReturningUser: boolean) => {
    // Only validate terms for new users (signup)
    if (!isReturningUser && !acceptedTerms) {
      setError(t('login.termsRequired'))
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      // Persist the intended destination (if any) so AuthCallback can restore it
      const fromState = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from
      if (fromState && fromState.pathname) {
        const intendedPath = `${fromState.pathname}${fromState.search ?? ''}`
        sessionStorage.setItem('post_login_redirect', intendedPath)
      }
      
      // Store consent data before OAuth redirect (OAuth redirects away from the page)
      // For returning users, we don't send consent - backend will reject if they're actually new
      sessionStorage.setItem('auth_consent', JSON.stringify({
        accepted_terms: isReturningUser ? false : acceptedTerms,
        accepted_marketing: isReturningUser ? false : acceptedMarketing
      }))
      
      await signInWithGoogle()
      // The actual navigation will happen in the AuthCallback component
    } catch (error) {
      console.error('Sign in failed:', error)
      setError(t('login.signInFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="login-page min-h-screen bg-gradient-to-br from-blue-100 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      {/* Decorative background blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-[600px] h-[600px] bg-blue-200/70 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-[600px] h-[600px] bg-indigo-200/70 rounded-full blur-3xl" />
      </div>

      <div className="login-container relative w-full max-w-md">
        {/* Logo and branding */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary mb-3 shadow-lg shadow-primary/20">
            <BookOpen className="w-7 h-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-stone-800 mb-1">
            {t('login.signupTitle')}
          </h1>
          <p className="text-sm text-stone-500">
            {t('login.subtitle')}
          </p>
        </div>

        {/* Main card */}
        <div className="login-card bg-white/70 backdrop-blur-sm border border-stone-200 rounded-2xl p-6 shadow-xl shadow-stone-200/50">
          
          {/* Value proposition */}
          <div className="value-props space-y-3 mb-6">
            <div className="value-prop flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-700 text-sm">{t('login.featureLearning')}</h3>
                <p className="text-xs text-stone-500">{t('login.featureLearningDesc')}</p>
              </div>
            </div>
            <div className="value-prop flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-700 text-sm">{t('login.featureTranslation')}</h3>
                <p className="text-xs text-stone-500">{t('login.featureTranslationDesc')}</p>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="error-alert flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 mb-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Consent checkboxes for new users */}
          <div className="consent-section space-y-3 mb-5">
            <div className="consent-terms flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                className="mt-0.5"
              />
              <label htmlFor="terms" className="text-sm text-stone-600 cursor-pointer leading-relaxed">
                {t('login.acceptTerms')}{' '}
                <Link to="/legal/terms" target="_blank" className="text-primary hover:text-primary/80 underline underline-offset-2">
                  {t('login.termsOfService')}
                </Link>
                {' '}{t('login.and')}{' '}
                <Link to="/legal/privacy" target="_blank" className="text-primary hover:text-primary/80 underline underline-offset-2">
                  {t('login.privacyPolicy')}
                </Link>
                <span className="text-red-500 ml-1">*</span>
              </label>
            </div>
            <div className="consent-marketing flex items-start gap-3">
              <Checkbox
                id="marketing"
                checked={acceptedMarketing}
                onCheckedChange={(checked) => setAcceptedMarketing(checked === true)}
                className="mt-0.5"
              />
              <label htmlFor="marketing" className="text-sm text-stone-700 cursor-pointer leading-relaxed">
                {t('login.acceptMarketing')}
              </label>
            </div>
          </div>

          {/* Google Sign Up Button */}
          <Button 
            onClick={() => handleGoogleAuth(false)}
            disabled={isLoading}
            variant="outline"
            className="google-signup-btn w-full h-12 bg-white hover:bg-stone-50 text-stone-700 font-medium rounded-xl border border-stone-300 shadow-sm transition-all duration-200 hover:shadow-md hover:border-stone-400 disabled:opacity-50"
            size="lg"
          >
            <GoogleIcon className="mr-3 h-5 w-5" />
            {isLoading ? t('login.signingIn') : t('login.signupWithGoogle')}
          </Button>

          {/* Sign in option for returning users - directly triggers Google OAuth */}
          <div className="signin-option mt-5 pt-5 border-t border-stone-200">
            <div className="text-center">
              <p className="text-sm text-stone-600 mb-3">
                {t('login.alreadyHaveAccount')}
              </p>
              <Button
                variant="outline"
                onClick={() => handleGoogleAuth(true)}
                disabled={isLoading}
                className="google-signin-btn w-full h-11 border-primary text-primary hover:bg-primary hover:text-white font-medium"
              >
                <GoogleIcon className="mr-2 h-4 w-4" />
                {isLoading ? t('login.signingIn') : t('login.signInWithGoogle')}
              </Button>
            </div>
          </div>

          {/* Security note */}
          <p className="text-center text-xs text-stone-400 mt-4">
            {t('login.secureAuth')}
          </p>
        </div>

        {/* Footer links */}
        <div className="footer-links text-center mt-6 text-xs text-stone-400">
          <Link to="/legal/terms" className="hover:text-stone-600 transition-colors">
            {t('footer.terms')}
          </Link>
          <span className="mx-2">•</span>
          <Link to="/legal/privacy" className="hover:text-stone-600 transition-colors">
            {t('footer.privacy')}
          </Link>
          <span className="mx-2">•</span>
          <Link to="/legal/cookies" className="hover:text-stone-600 transition-colors">
            {t('footer.cookies')}
          </Link>
        </div>
      </div>
    </div>
  )
}
